import React, { createContext, useCallback, useState, useContext } from "react";

export type SubmenuKeys = "home" | "orders" | "settings" | "reports";

type IsSubmenuOpenProps = {
  [key in SubmenuKeys]: boolean;
};

interface SidebarProviderProps {
  children: React.ReactNode;
}

interface SidebarContextProps {
  isSidebarMaximized: boolean;
  handleToggleSidebar(): void;
  setIsSubmenuOpen: React.Dispatch<React.SetStateAction<IsSubmenuOpenProps>>;
  isSubmenuOpen: IsSubmenuOpenProps;
  setIsSidebarMaximized: React.Dispatch<React.SetStateAction<boolean>>;
}

const SidebarContext = createContext<SidebarContextProps>(
  {} as SidebarContextProps,
);

export const SidebarProvider = ({ children }: SidebarProviderProps) => {
  const [isSidebarMaximized, setIsSidebarMaximized] = useState<boolean>(() => {
    let isSidebarMax: string | boolean | null =
      localStorage.getItem("@ctech:sidebar");

    if (isSidebarMax !== null) {
      isSidebarMax = Boolean(JSON.parse(isSidebarMax));
      return isSidebarMax;
    }
    return true;
  });

  const [isSubmenuOpen, setIsSubmenuOpen] = React.useState({
    orders: false,
    settings: false,
    reports: false,
    home: false,
  });

  const handleToggleSidebar = useCallback(() => {
    localStorage.setItem("@ctech:sidebar", (!isSidebarMaximized).toString());

    setIsSidebarMaximized((sidebarState) => {
      if (sidebarState) {
        setIsSubmenuOpen({
          orders: false,
          settings: false,
          reports: false,
          home: false,
        });
      }

      return !sidebarState;
    });
  }, [isSidebarMaximized]);

  return (
    <SidebarContext.Provider
      value={{
        isSidebarMaximized,
        handleToggleSidebar,
        setIsSidebarMaximized,
        isSubmenuOpen,
        setIsSubmenuOpen,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export function useSidebar(): SidebarContextProps {
  const context = useContext(SidebarContext);

  if (!context) {
    throw new Error("useSidebar must be used within a SidebarContext");
  }
  return context;
}
